import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Upload, FileText, Sparkles, Loader2, ArrowRight, Trash2, Clipboard, Copy, Download, ChevronRight, CheckCircle2 } from "lucide-react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { ScoreGauge, ScoreBar } from "@/components/ScoreGauge";
import PaywallPrompt from "@/components/PaywallPrompt";
import { useEntitlement } from "@/lib/entitlement-context";
import ProcessingProgress from "@/components/ProcessingProgress";
import { ANALYZE_STAGES, ALIGN_STAGES } from "@/lib/processing-stages";
import { useAuth } from "@/lib/auth-context";
import GuestGate from "@/components/GuestGate";
import { WorkspaceMismatchBanner, WorkspaceMismatchDialog } from "@/components/WorkspaceMismatch";
import { ResumeBuilderModal } from "@/components/ResumeBuilderModal";
import { SamplePicker } from "@/components/SamplePicker";
import { useWorkspace } from "@/lib/workspace-context";
// Sub-components (extracted from this file to keep the container focused
// on orchestration). All are pure presentational - behaviour unchanged.
import {
  ComponentBreakdown,
  AlignmentResults,
  CoverLetterModal,
  fmtDate,
} from "@/components/ResumeAlignment";


export default function ResumeAlignment() {
  const { user } = useAuth();
  if (user?.is_guest) return <GuestGate feature="align your own resume against a real job description" />;
  return <ResumeAlignmentInner />;
}

function ResumeAlignmentInner() {
  const { track } = useEntitlement();
  const { setActive: setActiveInline, activeId: wsActiveId, loading: wsLoading } = useWorkspace();
  const [resumes, setResumes] = useState([]);
  const [selectedResumeId, setSelectedResumeId] = useState(null);
  const [jobDescription, setJobDescription] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [analyzeStage, setAnalyzeStage] = useState("queued");
  const [analyzeStartedAt, setAnalyzeStartedAt] = useState(null);
  const [aligning, setAligning] = useState(false);
  const [alignJustCompleted, setAlignJustCompleted] = useState(false);
  const [alignStage, setAlignStage] = useState("queued");
  const [alignStartedAt, setAlignStartedAt] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [aligned, setAligned] = useState(null);
  const [error, setError] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [showSamples, setShowSamples] = useState(false);
  const [genJdBusy, setGenJdBusy] = useState(false);
  const [pasteName, setPasteName] = useState("");
  const [pasteContent, setPasteContent] = useState("");
  const [alignedHistory, setAlignedHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [openingHistoryId, setOpeningHistoryId] = useState(null);
  const [compareTargetId, setCompareTargetId] = useState(""); // "" = compare vs Original; otherwise an aligned_id
  const [compareTarget, setCompareTarget] = useState(null); // fetched aligned doc for compare
  const [mismatchDetail, setMismatchDetail] = useState(null); // 409 payload → confirm dialog
  const [mismatchDismissed, setMismatchDismissed] = useState(false); // inline banner dismissed for the session
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const loadResumes = async () => {
    const { data } = await api.get("/resume/list");
    setResumes(data);
    // Keep the current selection only when it exists in THIS workspace's list;
    // otherwise fall back to the newest resume (prevents cross-workspace ids).
    setSelectedResumeId((prev) =>
      prev && data.some((r) => r.resume_id === prev)
        ? prev
        : (data.length ? data[0].resume_id : null)
    );
  };

  const loadAlignedHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/resume/aligned");
      setAlignedHistory(data || []);
    } catch (e) {
      // History is a secondary surface - surface the error at debug level
      // but keep the page usable.
      console.warn("[ResumeAlignment] loadAlignedHistory failed", e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const [deletingHistoryId, setDeletingHistoryId] = useState(null);
  const [coverLetterOpen, setCoverLetterOpen] = useState(false);
  const [copiedAligned, setCopiedAligned] = useState(false);
  // Template picker for the download buttons. `auto` is the smart default -
  // backend picks Executive for CEO/COO/CTO/VP/Director/etc., Standard for
  // everyone else. Users can override at click-time.
  // Template picker retired - the backend now always renders the single
  // professional template, so no client-side selection is needed.
  const copyAlignedText = async () => {
    try {
      await navigator.clipboard.writeText(aligned?.aligned_resume || "");
      setCopiedAligned(true);
      setTimeout(() => setCopiedAligned(false), 1500);
    } catch (e) {
      console.debug("[ResumeAlignment] clipboard copy failed", e);
    }
  };
  const deleteAlignedHistory = async (id) => {
    if (!window.confirm("Delete this aligned resume from history? This cannot be undone.")) return;
    setDeletingHistoryId(id);
    try {
      await api.delete(`/resume/aligned/${id}`);
      await loadAlignedHistory();
    } catch (e) {
      console.warn("[ResumeAlignment] deleteAlignedHistory failed", e);
    } finally {
      setDeletingHistoryId(null);
    }
  };

  // Load workspace-scoped lists once the active workspace has hydrated and
  // reload on every workspace switch. A switch also clears any analysis /
  // aligned view left over from the previous workspace.
  const wsFirstLoadRef = useRef(true);
  useEffect(() => {
    if (wsLoading) return;
    if (!wsFirstLoadRef.current) {
      setAnalysis(null);
      setAligned(null);
      setCompareTargetId("");
      setCompareTarget(null);
    }
    wsFirstLoadRef.current = false;
    loadResumes();
    loadAlignedHistory();
  }, [wsLoading, wsActiveId]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the main aligned view changes, reset comparison back to "Original".
  useEffect(() => {
    setCompareTargetId("");
    setCompareTarget(null);
  }, [aligned?.aligned_id]);

  // Fetch the compare-target aligned doc whenever the user picks a different one.
  useEffect(() => {
    let cancelled = false;
    if (!compareTargetId) {
      setCompareTarget(null);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/resume/aligned/${compareTargetId}`);
        if (!cancelled) setCompareTarget(data);
      } catch (e) {
        if (!cancelled) {
          console.warn("[ResumeAlignment] compareTarget fetch failed", e);
          setCompareTarget(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [compareTargetId]);

  const selectedResume = resumes.find((r) => r.resume_id === selectedResumeId);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    track("resume.upload_started", { source: "file", filename: file?.name || null });
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/resume/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await loadResumes();
      setSelectedResumeId(data.resume_id);
      track("resume.upload_completed", { resume_id: data.resume_id, source: "file" });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onSaveText = async () => {
    setError("");
    if (!pasteName.trim() || !pasteContent.trim()) {
      setError("Please provide both a name and content.");
      return;
    }
    setUploading(true);
    try {
      const { data } = await api.post("/resume/save-text", {
        name: pasteName.trim(),
        content: pasteContent,
      });
      await loadResumes();
      setSelectedResumeId(data.resume_id);
      setShowPaste(false);
      setPasteName("");
      setPasteContent("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this resume?")) return;
    await api.delete(`/resume/${id}`);
    await loadResumes();
    if (selectedResumeId === id) {
      setSelectedResumeId(null);
      setAnalysis(null);
      setAligned(null);
    }
  };

  const onAnalyze = async () => {
    setError("");
    if (!selectedResumeId) {
      setError("Please upload or select a resume first.");
      return;
    }
    if (jobDescription.trim().length < 30) {
      setError("Please paste a meaningful job description (30+ characters).");
      return;
    }
    setAnalyzing(true);
    setAnalysis(null);
    setAligned(null);
    await _runAnalyze({ confirmMismatch: false });
  };

  // Auto-detect role + company from the pasted JD so the mismatch guard has
  // real data to compare against (users would otherwise leave these blank
  // and unknowingly bypass the check). Runs client-side debounced whenever
  // the JD grows past 60 chars AND both title/company are empty, or when
  // the user just cleared one of them.
  const parsedJDSourceRef = useRef(""); // last JD text we successfully parsed
  useEffect(() => {
    const jd = jobDescription.trim();
    if (jd.length < 60) return undefined;
    // Only fill when at least one field is still blank; never overwrite
    // what the user has typed.
    if (jobTitle && company) return undefined;
    if (parsedJDSourceRef.current === jd) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/resume/parse-jd", { job_description: jd });
        if (cancelled) return;
        parsedJDSourceRef.current = jd;
        if (!jobTitle && data?.job_title) setJobTitle(data.job_title);
        if (!company && data?.company) setCompany(data.company);
      } catch (e) {
        // Silent - pre-fill is a nice-to-have, not a blocker.
        console.debug("[ResumeAlignment] parse-jd auto-fill failed", e);
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [jobDescription, jobTitle, company]);

  // Broken out so both the initial submit and the "continue anyway" retry
  // from the workspace-mismatch dialog can call it with the same payload.
  //
  // The analyze endpoint is async: POST returns {analysis_id, status:"processing"}
  // in <1s, then we poll GET /resume/analyses/{id} until status is "ready"
  // or "failed". This decouples the wall time of the LLM extraction from
  // the HTTP request lifetime so no request can be cut short by the
  // Cloudflare 100s edge timeout - the connection is never held open
  // longer than a few hundred ms per call.
  const _runAnalyze = async ({ confirmMismatch }) => {
    try {
      const { data: kickoff } = await api.post("/resume/analyze", {
        resume_id: selectedResumeId,
        job_description: jobDescription,
        job_title: jobTitle || null,
        company: company || null,
        confirm_workspace_mismatch: !!confirmMismatch,
      });

      // If the server returned a fully-hydrated doc (legacy contract or
      // beta_auto path), display it and stop. Otherwise poll the job.
      if (kickoff?.status && kickoff.status !== "processing") {
        setAnalysis(kickoff);
        setMismatchDetail(null);
        setAnalyzing(false);
        return;
      }
      setMismatchDetail(null);

      const analysisId = kickoff?.analysis_id;
      if (!analysisId) throw new Error("Missing analysis_id from server");
      setAnalyzingId(analysisId);
      setAnalyzeStage(kickoff?.stage || "queued");
      setAnalyzeStartedAt(kickoff?.created_at || new Date().toISOString());

      const startedAt = Date.now();
      const maxWaitMs = 180_000; // 3 min hard cap → controlled error, no stuck UI
      // Fast polling: backend job takes ~10-30s; a 1s interval keeps us within
      // ~1s of true completion without meaningfully increasing load.
      const pollIntervalMs = 1000;
      const firstPollDelayMs = 500;

      const poll = async () => {
        if (Date.now() - startedAt > maxWaitMs) {
          setError(
            "Analysis is taking longer than expected. Please try again in a moment."
          );
          setAnalyzing(false);
          setAnalyzingId(null);
          return;
        }
        try {
          const { data: doc } = await api.get(`/resume/analyses/${analysisId}`);
          if (doc?.stage) setAnalyzeStage(doc.stage);
          if (doc?.status === "ready") {
            setAnalysis(doc);
            setAnalyzeStage("ready");
            track("resume.analysis_viewed", {
              analysis_id: doc.analysis_id,
              alignment_score: doc.alignment_score,
            });
            track("resume.ats_viewed", {
              analysis_id: doc.analysis_id,
              ats_score: doc.ats_score,
            });
            setAnalyzing(false);
            setAnalyzingId(null);
            return;
          }
          if (doc?.status === "failed") {
            setError(
              doc.error_message ||
                "AI analysis is temporarily unavailable. Please retry in a moment."
            );
            setAnalyzing(false);
            setAnalyzingId(null);
            return;
          }
          if (doc?.status === "cancelled") {
            // User cancelled - leave UI in the pre-analyze state.
            setAnalyzing(false);
            setAnalyzingId(null);
            return;
          }
        } catch (pollErr) {
          // Transient network / 5xx during poll - keep trying until maxWait.
          console.debug("[analyze] poll failed, retrying", pollErr?.message);
        }
        setTimeout(poll, pollIntervalMs);
      };
      setTimeout(poll, firstPollDelayMs);
    } catch (err) {
      if (
        err?.response?.status === 409 &&
        err?.response?.data?.detail?.error === "workspace_mismatch"
      ) {
        setMismatchDetail(err.response.data.detail);
      } else {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
      setAnalyzing(false);
      setAnalyzingId(null);
    }
  };

  const onCancelAnalyze = async () => {
    if (!analyzingId) return;
    try {
      await api.post(`/resume/analyses/${analyzingId}/cancel`);
      // Poll loop notices `status: "cancelled"` on its next tick and
      // resets UI state - but reset here too for instant feedback.
      setAnalyzing(false);
      setAnalyzingId(null);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Could not cancel analysis.");
    }
  };

  const onAlign = async () => {
    if (!analysis) return;
    setAligning(true);
    setAlignJustCompleted(false);
    setError("");
    setAlignStage("queued");
    try {
      const { data: kickoff } = await api.post("/resume/align", { analysis_id: analysis.analysis_id });
      // The kickoff response is a stub. Seed the aligned card with what we
      // know so the UI can render the "Aligning…" state and the Cancel
      // button. Poll fills in aligned_resume + change_notes + scores.
      setAligned(kickoff);
      setAlignStage(kickoff?.stage || "queued");
      setAlignStartedAt(kickoff?.created_at || new Date().toISOString());

      if (kickoff?.align_status === "processing" && kickoff?.aligned_id) {
        pollAlign(kickoff.aligned_id);
      } else if (kickoff?.rescore_status === "pending" && kickoff?.aligned_id) {
        // Legacy shape (align already done, only rescore pending)
        setAlignJustCompleted(true);
        pollRescore(kickoff.aligned_id);
      }
      loadAlignedHistory();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setAligning(false);
    }
  };

  // Poll the aligned doc through both lifecycles:
  //   1. align_status: processing → ready|failed|cancelled
  //   2. rescore_status: pending → ready|failed|cancelled  (chained after align ready)
  // Only updates state if the poll refers to the currently-open aligned doc
  // - a stale poll from a previous align must NEVER clobber the view.
  const pollAlign = React.useCallback(async (aligned_id) => {
    const started = Date.now();
    const maxMs = 180_000;
    let alignReadyAt = null;

    const tick = async () => {
      if (Date.now() - started > maxMs) {
        setError("Alignment is taking longer than expected. Please try again.");
        setAligning(false);
        return;
      }
      try {
        const { data } = await api.get(`/resume/aligned/${aligned_id}`);
        // Ignore this tick if the user has since navigated to a different
        // aligned doc - otherwise a stale poll can flash old scores over
        // the newer view.
        setAligned((cur) => (cur?.aligned_id === aligned_id ? data : cur));
        if (data?.stage) setAlignStage(data.stage);

        const as = data?.align_status;
        const rs = data?.rescore_status;

        if (as === "failed") {
          setError(data.error_message || "AI alignment is temporarily unavailable. Please retry in a moment.");
          setAligning(false);
          return;
        }
        if (as === "cancelled") {
          // User (or a competing re-align) cancelled - leave UI clean.
          setAligning(false);
          return;
        }
        if (as === "ready" && !alignReadyAt) {
          alignReadyAt = Date.now();
          setAligning(false);              // aligned resume visible → button no longer says "Improving…"
          setAlignJustCompleted(true);
        }
        if (as === "ready" && (rs === "ready" || rs === "failed" || rs === "cancelled")) {
          return; // fully done
        }
      } catch (e) {
        console.debug("[align] poll failed", e?.message);
      }
      setTimeout(tick, 1000);
    };
    setTimeout(tick, 500);
  }, []);

  const onCancelAlign = async () => {
    if (!aligned?.aligned_id) return;
    try {
      await api.post(`/resume/aligned/${aligned.aligned_id}/cancel`);
      setAligned(null);
      setAligning(false);
      setAlignJustCompleted(false);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Could not cancel alignment.");
    }
  };

  const pollRescore = React.useCallback(async (aligned_id) => {
    const started = Date.now();
    const tick = async () => {
      if (Date.now() - started > 60_000) return; // give up quietly
      try {
        const { data } = await api.get(`/resume/aligned/${aligned_id}`);
        if (data?.rescore_status && data.rescore_status !== "pending") {
          // Update ONLY if this poll refers to the currently-open aligned doc,
          // so a stale poll from a previous align doesn't clobber the view.
          setAligned((cur) => (cur?.aligned_id === aligned_id ? data : cur));
          return;
        }
      } catch (e) {
        console.debug("[align] rescore poll failed", e?.message);
      }
      setTimeout(tick, 1000);
    };
    setTimeout(tick, 500);
  }, []);

  const goToPrep = () => {
    if (!analysis) return;
    navigate(`/app/prep?analysis_id=${analysis.analysis_id}`);
  };

  const openHistoryItem = async (aligned_id) => {
    setOpeningHistoryId(aligned_id);
    setError("");
    try {
      const { data } = await api.get(`/resume/aligned/${aligned_id}`);
      setAligned(data);
      // Bring the aligned card into view
      setTimeout(() => {
        document
          .querySelector("[data-testid='aligned-result']")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setOpeningHistoryId(null);
    }
  };

  return (
    <div data-testid="resume-alignment-page" className="animate-in">
      <ResumeBuilderModal
        open={showBuilder}
        onClose={() => setShowBuilder(false)}
        onUse={async (data) => {
          await loadResumes();
          setSelectedResumeId(data.resume_id);
        }}
      />
      <SamplePicker
        open={showSamples}
        onClose={() => setShowSamples(false)}
        onStartInterview={(sampleId) => navigate(`/app/live?sample=${sampleId}`)}
        onUseInAlignment={async (pack) => {
          try {
            const { data } = await api.post("/resume/save-text", {
              name: `Sample, ${pack.candidate} (${pack.role})`,
              content: pack.resume,
            });
            await loadResumes();
            setSelectedResumeId(data.resume_id);
          } catch { /* ignore */ }
          setJobTitle(pack.role);
          setCompany(pack.company);
          setJobDescription(pack.job_description);
        }}
      />
      <WorkspaceMismatchDialog
        detail={mismatchDetail}
        jd={jobDescription}
        onSwitch={() => {
          // The dialog stays open until the setActive-driven state update
          // completes. Firing the retry after a microtask ensures the axios
          // interceptor picks up the new X-Workspace-Id header.
          if (mismatchDetail?.suggested_workspace_id) {
            // Import path avoided to keep this scoped - use a small inline
            // dispatcher via localStorage + reload-safe approach.
            // Simpler: call setActive via context.
            // We inline-import the hook at file level; call `setActiveInline`.
            setActiveInline(mismatchDetail.suggested_workspace_id);
            setMismatchDetail(null);
            setAnalyzing(true);
            setTimeout(() => _runAnalyze({ confirmMismatch: false }), 100);
          }
        }}
        onCreated={() => {
          // A new workspace was just created for this role/company and
          // activated. Close the dialog and retry the analyze - the new
          // workspace now matches, so the guard will pass without a
          // confirm flag.
          setMismatchDetail(null);
          setAnalyzing(true);
          setTimeout(() => _runAnalyze({ confirmMismatch: false }), 150);
        }}
        onContinue={() => {
          setMismatchDetail(null);
          setAnalyzing(true);
          _runAnalyze({ confirmMismatch: true });
        }}
        onCancel={() => setMismatchDetail(null)}
      />
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-2">
          01 · Stand Out
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-50">
          Will your resume get you the interview?
        </h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
          Get your application ready for this role in three moves: analyze how well your current
          resume matches the job, improve it based on the requirements, and create a matching
          cover letter, all truthful, all yours.
        </p>
        <div data-testid="application-ready-strip" className="mt-4 flex flex-wrap items-center gap-2">
          {[
            { n: "1", t: "Analyze Fit", d: "Understand your match" },
            { n: "2", t: "Improve My Resume", d: "Strengthen your application" },
            { n: "3", t: "Create My Cover Letter", d: "Complete your application" },
          ].map((s, i) => (
            <React.Fragment key={s.n}>
              {i > 0 && <ChevronRight className="h-4 w-4 text-zinc-600 flex-shrink-0" />}
              <span className="inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-900/60 px-3 py-1.5">
                <span className="h-5 w-5 rounded-full bg-amber-500/15 border border-amber-500/30 text-[10px] font-bold text-amber-300 flex items-center justify-center">{s.n}</span>
                <span className="text-xs font-semibold text-zinc-100">{s.t}</span>
                <span className="hidden sm:inline text-[11px] text-zinc-500">· {s.d}</span>
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {error && (
        <div
          data-testid="resume-error"
          className="mb-6 text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-4 py-3"
        >
          {error}
        </div>
      )}

      {/* Split pane */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: Resume */}
        <div className="space-y-5">
          <div className="p-5 rounded-xl border border-zinc-800/70 bg-zinc-900/40">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Your resume</div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="resume-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="text-xs inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-medium rounded-md px-2.5 py-1.5 transition-colors"
                >
                  {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  Upload
                </button>
                <button
                  data-testid="resume-paste-btn"
                  onClick={() => setShowPaste((v) => !v)}
                  className="text-xs inline-flex items-center gap-1.5 border border-zinc-800 hover:bg-zinc-900 text-zinc-300 font-medium rounded-md px-2.5 py-1.5 transition-colors"
                >
                  <Clipboard className="h-3 w-3" /> Paste
                </button>
                <button
                  data-testid="resume-ai-create-btn"
                  onClick={() => setShowBuilder(true)}
                  className="text-xs inline-flex items-center gap-1.5 border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 font-medium rounded-md px-2.5 py-1.5 transition-colors"
                >
                  <Sparkles className="h-3 w-3" /> Create with AI
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt"
                  className="hidden"
                  onChange={onFileChange}
                  data-testid="resume-file-input"
                />
              </div>
            </div>

            {showPaste ? (
              <div className="space-y-3">
                <input
                  data-testid="resume-paste-name-input"
                  type="text"
                  placeholder="Name (e.g., Jordan Rivera - 2026 Resume)"
                  value={pasteName}
                  onChange={(e) => setPasteName(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none"
                />
                <textarea
                  data-testid="resume-paste-content-input"
                  placeholder="Paste your resume text here…"
                  value={pasteContent}
                  onChange={(e) => setPasteContent(e.target.value)}
                  rows={12}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none font-mono leading-relaxed"
                />
                <div className="flex gap-2">
                  <button
                    data-testid="resume-paste-save-btn"
                    onClick={onSaveText}
                    disabled={uploading}
                    className="text-xs bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-md px-3 py-1.5"
                  >
                    Save resume
                  </button>
                  <button
                    onClick={() => setShowPaste(false)}
                    className="text-xs border border-zinc-800 text-zinc-400 rounded-md px-3 py-1.5"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : resumes.length === 0 ? (
              <div data-testid="resume-empty" className="space-y-2.5">
                <button
                  data-testid="choose-have-resume"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full text-left rounded-xl border border-zinc-800 bg-zinc-950/60 hover:border-zinc-600 px-4 py-3.5 transition-colors"
                >
                  <div className="text-sm text-zinc-100 font-medium flex items-center gap-2"><Upload className="h-4 w-4 text-zinc-400" /> I have a resume</div>
                  <div className="text-xs text-zinc-500 mt-0.5 ml-6">Upload a PDF or DOCX (or use Paste above)</div>
                </button>
                <button
                  data-testid="choose-no-resume"
                  onClick={() => setShowBuilder(true)}
                  className="w-full text-left rounded-xl border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 px-4 py-3.5 transition-colors"
                >
                  <div className="text-sm text-amber-200 font-medium flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-400" /> I don't have a resume</div>
                  <div className="text-xs text-zinc-500 mt-0.5 ml-6">Create My Resume with AI, just tell us about your experience</div>
                </button>
                <button
                  data-testid="choose-sample"
                  onClick={() => setShowSamples(true)}
                  className="w-full text-left rounded-xl border border-zinc-800 bg-zinc-950/60 hover:border-zinc-600 px-4 py-3.5 transition-colors"
                >
                  <div className="text-sm text-zinc-100 font-medium flex items-center gap-2"><ArrowRight className="h-4 w-4 text-zinc-400" /> I just want to try Intuiterview</div>
                  <div className="text-xs text-zinc-500 mt-0.5 ml-6">Explore with a complete sample candidate, no documents needed</div>
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2 items-center">
                  {resumes.map((r) => (
                    <button
                      key={r.resume_id}
                      data-testid={`resume-chip-${r.resume_id}`}
                      onClick={() => setSelectedResumeId(r.resume_id)}
                      className={`text-xs px-3 py-1.5 rounded-md border transition-all ${
                        selectedResumeId === r.resume_id
                          ? "bg-amber-500/10 border-amber-500/40 text-amber-300"
                          : "border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700"
                      }`}
                    >
                      {r.name}
                    </button>
                  ))}
                  <Link
                    to="/app/resumes"
                    data-testid="resume-manage-all-link"
                    className="text-[11px] text-zinc-500 hover:text-zinc-200 underline underline-offset-4 ml-1"
                  >
                    Manage all resumes →
                  </Link>
                </div>
                {selectedResume && (
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4 max-h-72 overflow-y-auto">
                    <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-widest text-zinc-500">
                      <span>{selectedResume.name}</span>
                      <button
                        data-testid={`resume-delete-${selectedResume.resume_id}`}
                        onClick={() => onDelete(selectedResume.resume_id)}
                        className="p-1 hover:text-rose-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed">
                      {selectedResume.content}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: JD + Scores */}
        <div className="space-y-5">
          <div className="p-5 rounded-xl border border-zinc-800/70 bg-zinc-900/40 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Job Description</div>
            <WorkspaceMismatchBanner
              role={jobTitle}
              company={company}
              jd={jobDescription}
              dismissed={mismatchDismissed}
              onDismiss={() => setMismatchDismissed(true)}
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                data-testid="job-title-input"
                type="text"
                placeholder="Job title (optional)"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none"
              />
              <input
                data-testid="company-input"
                type="text"
                placeholder="Company (optional)"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none"
              />
            </div>
            <textarea
              data-testid="job-description-input"
              rows={10}
              placeholder="Paste the full job description…"
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none leading-relaxed"
            />
            {!jobDescription.trim() && (
              <button
                data-testid="generate-jd-btn"
                disabled={genJdBusy || !jobTitle.trim()}
                onClick={async () => {
                  setGenJdBusy(true);
                  try {
                    const { data } = await api.post("/resume/builder/jd", { job_title: jobTitle, company: company || null });
                    setJobDescription(data.job_description || "");
                  } catch { /* keep quiet */ } finally { setGenJdBusy(false); }
                }}
                className="text-xs inline-flex items-center gap-1.5 border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 rounded-md px-2.5 py-1.5 disabled:opacity-50"
              >
                {genJdBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                {jobTitle.trim() ? "No job description? Create it with AI" : "Enter a job title above, then create the JD with AI"}
              </button>
            )}
            <div className="flex items-stretch gap-2">
              <button
                data-testid="analyze-btn"
                onClick={onAnalyze}
                disabled={analyzing || !selectedResumeId}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-amber-950 disabled:opacity-60 font-semibold rounded-lg px-4 py-2.5 transition-colors"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Analyzing…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" /> Analyze fit
                  </>
                )}
              </button>
              {analyzing && analyzingId && (
                <button
                  data-testid="cancel-analyze-btn"
                  type="button"
                  onClick={onCancelAnalyze}
                  className="inline-flex items-center gap-1.5 text-sm border border-zinc-700 hover:border-rose-500/40 hover:bg-rose-500/[0.08] hover:text-rose-200 text-zinc-300 rounded-lg px-3 py-2 transition-colors"
                  title="Cancel analysis and free the slot"
                >
                  Cancel
                </button>
              )}
            </div>
            {analyzing && (
              <ProcessingProgress
                testId="analyze-progress"
                title="Analyzing your resume"
                stages={ANALYZE_STAGES}
                currentStage={analyzeStage}
                status="processing"
                startedAt={analyzeStartedAt}
              />
            )}
          </div>
        </div>
      </div>

      {/* Fit Analysis — full-width, balanced layout */}
      {analysis && (
        <div data-testid="analysis-result" className="mt-6 space-y-6 animate-in">
          {/* Overall Match + Alignment Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="p-5 rounded-xl border border-zinc-800/70 bg-zinc-900/40 space-y-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Overall match</div>
              <div className="flex items-center gap-6">
                <ScoreGauge value={analysis.match_score} label="job match" testid="match-gauge" infoVariant="match" />
                <div className="flex-1 space-y-4">
                  <ScoreBar label="ATS Score" value={analysis.ats_score} testid="ats-bar" infoVariant="ats" />
                </div>
              </div>
              <div className="text-sm text-zinc-300 leading-relaxed">{analysis.summary}</div>
            </div>
            <div className="p-5 rounded-xl border border-zinc-800/70 bg-zinc-900/40">
              <ComponentBreakdown
                components={analysis.component_scores}
                maxes={analysis.component_max}
                total={analysis.match_score}
              />
            </div>
          </div>

          {/* Keywords + Strengths + Growth opportunities */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-5 rounded-xl border border-zinc-800/70 bg-zinc-900/40 space-y-5">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">
                  Matched keywords
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(analysis.matched_keywords || []).map((k) => (
                    <span
                      key={k}
                      className="text-[11px] bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 rounded-md px-2 py-0.5"
                    >
                      {k}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-rose-400 mb-2">
                  Missing keywords
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(analysis.missing_keywords || []).map((k) => (
                    <span
                      key={k}
                      className="text-[11px] bg-rose-500/10 text-rose-300 border border-rose-500/20 rounded-md px-2 py-0.5"
                    >
                      {k}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-5 rounded-xl border border-zinc-800/70 bg-zinc-900/40">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                Strengths
              </div>
              <ul className="text-xs text-zinc-300 space-y-1.5 leading-relaxed">
                {(analysis.strengths || []).map((s, i) => (
                  <li key={i} className="flex gap-2"><span className="text-emerald-400">▸</span>{s}</li>
                ))}
              </ul>
            </div>
            <div className="p-5 rounded-xl border border-zinc-800/70 bg-zinc-900/40">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                Growth opportunities
              </div>
              <ul className="text-xs text-zinc-300 space-y-1.5 leading-relaxed">
                {(analysis.gaps || []).map((s, i) => (
                  <li key={i} className="flex gap-2"><span className="text-amber-400">▸</span>{s}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Recommendations + Next steps */}
          <div className="p-5 rounded-xl border border-zinc-800/70 bg-zinc-900/40">
            <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                  Recommendations
                </div>
                <ul className="text-xs text-zinc-300 space-y-1.5 leading-relaxed">
                  {(analysis.recommendations || []).map((s, i) => (
                    <li key={i} className="flex gap-2"><span className="text-amber-400">▸</span>{s}</li>
                  ))}
                </ul>
              </div>
              <div className="lg:border-l lg:border-zinc-800 lg:pl-6">
                <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-3">
                  Next steps
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    data-testid="align-btn"
                    onClick={onAlign}
                    disabled={aligning}
                    className="inline-flex items-center justify-center gap-2 bg-zinc-50 text-zinc-950 hover:bg-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
                  >
                    {aligning ? (
                      <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Improving Resume…</>
                    ) : alignJustCompleted && aligned?.align_status === "ready" ? (
                      <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Resume Improved</>
                    ) : (
                      <><Sparkles className="h-3.5 w-3.5" /> Improve My Resume</>
                    )}
                  </button>
                  {alignJustCompleted && !aligning && aligned?.align_status === "ready" && (
                    <div
                      data-testid="align-success-banner"
                      className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.08] px-3 py-2.5 text-xs text-emerald-200 space-y-2"
                    >
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-emerald-400" />
                        <span>
                          Your improved resume is ready.
                          {Number.isFinite(aligned?.original_scores?.match_score) &&
                            Number.isFinite(aligned?.updated_scores?.match_score) && (
                              <span className="block text-emerald-300/90 mt-0.5 tabular-nums">
                                Job Match {aligned.original_scores.match_score} → {aligned.updated_scores.match_score}
                              </span>
                            )}
                        </span>
                      </div>
                      <button
                        type="button"
                        data-testid="view-improved-resume-btn"
                        onClick={() => {
                          document
                            .querySelector("[data-testid='aligned-result']")
                            ?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }}
                        className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold rounded-md px-3 py-1.5 text-xs transition-colors"
                      >
                        View Improved Resume ↓
                      </button>
                    </div>
                  )}
                  {aligning && aligned?.aligned_id && (
                    <button
                      data-testid="cancel-align-btn"
                      type="button"
                      onClick={onCancelAlign}
                      className="inline-flex items-center justify-center gap-1.5 border border-zinc-700 hover:border-rose-500/40 hover:bg-rose-500/[0.08] hover:text-rose-200 text-zinc-300 rounded-lg px-3 py-2 text-sm transition-colors"
                      title="Cancel alignment and free the slot"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    data-testid="continue-to-prep-btn"
                    onClick={goToPrep}
                    className="inline-flex items-center justify-center gap-2 border border-zinc-800 hover:bg-zinc-900 text-zinc-200 rounded-lg px-4 py-2 text-sm font-medium"
                  >
                    Continue to Interview Prep <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {analysis && <PaywallPrompt matchScore={analysis.match_score} />}

      {/* Aligned resume results panel (extracted to
          components/ResumeAlignment/AlignmentResults.jsx) */}
      <div className="mt-6">
        <AlignmentResults
          aligned={aligned}
          alignedHistory={alignedHistory}
          compareTargetId={compareTargetId}
          setCompareTargetId={setCompareTargetId}
          compareTarget={compareTarget}
          alignStage={alignStage}
          alignStartedAt={alignStartedAt}
          onCancelAlign={onCancelAlign}
          copyAlignedText={copyAlignedText}
          copiedAligned={copiedAligned}
          setCoverLetterOpen={setCoverLetterOpen}
        />
      </div>

      {/* Previous Alignments, history with per-item download */}
      <div className="mt-10">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-1.5 font-medium">
              Previous Alignments
            </div>
            <div className="text-sm text-zinc-400 leading-relaxed">
              Reopen any past alignment or download the tailored resume.
            </div>
          </div>
          {alignedHistory.length > 0 && (
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 tabular-nums">
              {alignedHistory.length} saved
            </div>
          )}
        </div>

        {historyLoading ? (
          <div
            data-testid="aligned-history-loading"
            className="p-5 rounded-xl border border-zinc-800/70 bg-zinc-900/40 text-sm text-zinc-500 flex items-center gap-2"
          >
            <Loader2 className="h-4 w-4 animate-spin" /> Loading history…
          </div>
        ) : alignedHistory.length === 0 ? (
          <div
            data-testid="aligned-history-empty"
            className="p-6 rounded-xl border border-zinc-800/70 bg-zinc-900/40 text-sm text-zinc-500"
          >
            You haven&apos;t created any improved resumes yet. Analyze a job description above and run &quot;Improve My Resume&quot; to see it here.
          </div>
        ) : (
          <ul data-testid="aligned-history-list" className="divide-y divide-zinc-800/60 rounded-xl border border-zinc-800/70 bg-zinc-900/40 overflow-hidden">
            {alignedHistory.map((h) => {
              const title = [h.job_title, h.company].filter(Boolean).join(" @ ") || "Aligned resume";
              const isOpening = openingHistoryId === h.aligned_id;
              return (
                <li
                  key={h.aligned_id}
                  data-testid={`aligned-history-item-${h.aligned_id}`}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 px-4 py-3 hover:bg-zinc-900/70 transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => openHistoryItem(h.aligned_id)}
                    data-testid={`aligned-history-open-${h.aligned_id}`}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left group w-full sm:w-auto"
                  >
                    <div className="h-9 w-9 rounded-md bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-3.5 w-3.5 text-emerald-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-zinc-100 font-medium truncate group-hover:text-white transition-colors">
                        {title}
                      </div>
                      <div className="text-[11px] text-zinc-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        <span>{fmtDate(h.created_at)}</span>
                        {h.change_count > 0 && (
                          <span>{h.change_count} change{h.change_count === 1 ? "" : "s"}</span>
                        )}
                        {h.resume_filename && <span className="truncate">from {h.resume_filename}</span>}
                      </div>
                    </div>
                  </button>

                  <div className="flex items-center gap-2 justify-end sm:justify-start flex-shrink-0 pl-12 sm:pl-0">
                    {typeof h.match_score === "number" && (
                      <div
                        className={`text-[11px] font-semibold tabular-nums px-2 py-0.5 rounded-md border ${
                          h.match_score >= 80
                            ? "text-emerald-300 border-emerald-500/25 bg-emerald-500/[0.06]"
                            : h.match_score >= 55
                            ? "text-amber-300 border-amber-500/25 bg-amber-500/[0.05]"
                            : "text-rose-300 border-rose-500/25 bg-rose-500/[0.05]"
                        }`}
                        title="Match score at time of alignment"
                      >
                        {h.match_score}%
                      </div>
                    )}

                    <a
                      href={`${API_BASE}/resume/aligned/${h.aligned_id}/download?format=docx`}
                      data-testid={`aligned-history-download-${h.aligned_id}`}
                      className="inline-flex items-center gap-1.5 text-xs bg-zinc-800 hover:bg-emerald-500 hover:text-emerald-950 text-zinc-200 font-medium rounded-md px-3 py-1.5 transition-colors flex-shrink-0"
                      title="Download tailored resume (.docx)"
                    >
                      {isOpening ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Download className="h-3 w-3" />
                      )}
                      <span className="hidden sm:inline">Download</span>
                      <span>.docx</span>
                    </a>
                    <button
                      type="button"
                      onClick={() => deleteAlignedHistory(h.aligned_id)}
                      disabled={deletingHistoryId === h.aligned_id}
                      data-testid={`aligned-history-delete-${h.aligned_id}`}
                      title="Delete this aligned resume"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-md text-zinc-500 hover:text-rose-300 hover:bg-rose-500/[0.08] transition-colors flex-shrink-0 disabled:opacity-60"
                    >
                      {deletingHistoryId === h.aligned_id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {coverLetterOpen && aligned && (
        <CoverLetterModal
          analysisId={aligned.analysis_id}
          alignedId={aligned.aligned_id}
          onClose={() => setCoverLetterOpen(false)}
        />
      )}
    </div>
  );
}

